import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@core/auth/services/auth.service';
import { environment } from '@env/environment';

/**
 * Functional Interceptor to attach JWT Bearer token to the Authorization header.
 * Only applied to requests targeting the auth or account APIs — avoids leaking
 * the token to unrelated or third-party domains.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const isCrossDomainAuthRequest =
    req.url.startsWith(environment.apiAuthUrl) ||
    req.url.startsWith(environment.apiAccountUrl);

  // In local/dev environments also attach the token to the base API,
  // since the HttpOnly cookie may not be set by the local backend.
  const isLocalBaseRequest =
    !environment.production && req.url.startsWith(environment.apiBaseUrl);

  if (!isCrossDomainAuthRequest && !isLocalBaseRequest) {
    return next(req);
  }

  const authService = inject(AuthService);

  // In local: try reading the cookie directly first (works if local backend
  // doesn't set HttpOnly), then fall back to the in-memory/sessionStorage token.
  const token = isLocalBaseRequest
    ? (authService.getToken() ?? authService.getAccessToken())
    : authService.getAccessToken();

  if (token) {
    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
    return next(authReq);
  }

  return next(req);
};
